// src/components/AuthSwitch.tsx
import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Box,
  TextField,
  Button,
  Typography,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { Visibility, VisibilityOff, Google } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../redux/store";
import {
  listenForAuthChanges,
  loginWithEmail,
  loginWithGoogle,
  registerWithEmail,
  sendResetEmail,
} from "../redux/authSlice";
import { useNavigate } from "react-router-dom";
import logoImg from "../assets/app/logo.png";

/* ─────────────────────────── fonts & keyframes ──────────────────────────── */
const FONT_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,400&display=swap');

  @keyframes gradSpin {
    0%   { background-position: 0% 50%; }
    50%  { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
  @keyframes shimmer {
    0%   { background-position: -200% center; }
    100% { background-position:  200% center; }
  }
  @keyframes pulse-ring {
    0%, 100% { opacity: 1; }
    50%       { opacity: 0.6; }
  }
`;

/* ─────────────────────────── theme colours ──────────────────────────────── */
const GRAD       = "linear-gradient(90deg, #007BFF 0%, #6A00FF 50%, #FF00D4 100%)";
const GRAD_ANIM  = "linear-gradient(90deg, #007BFF, #6A00FF, #FF00D4, #6A00FF, #007BFF)";
const BORDER_CLR = "#e4e2f0";
const MUTED_CLR  = "#6b6880";

/* ─────────────────────────── MUI field sx factory ──────────────────────── */
const fsx = () => ({
  mb: 2,
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    fontSize: 13,
    fontWeight: 500,
    color: "#07050f",
    background: "#fafafa",
    "& fieldset":             { borderColor: "#e4e2f0" },
    "&:hover fieldset":       { borderColor: "#6A00FF" },
    "&.Mui-focused fieldset": {
      borderColor: "transparent",
      borderWidth: "2px",
      // gradient border via box-shadow trick
      boxShadow: "0 0 0 2px #6A00FF",
    },
  },
  "& .MuiInputLabel-root": {
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    fontSize: 13,
    fontWeight: 500,
    color: MUTED_CLR,
    "&.Mui-focused": { color: "#6A00FF" },
  },
  "& .MuiFormHelperText-root": {
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    fontSize: 11,
    letterSpacing: "0.03em",
    color: MUTED_CLR,
  },
});

/* ════════════════════════════ COMPONENT ════════════════════════════════════ */
const AuthSwitch: React.FC = () => {
  const [mode, setMode]                       = useState<"login" | "signup">("login");
  const [email, setEmail]                     = useState("");
  const [password, setPassword]               = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword]       = useState(false);
  const [error, setError]                     = useState("");

  /* ── inline forgot-password state ── */
  const [showReset, setShowReset]     = useState(false);
  const [resetEmail, setResetEmail]   = useState("");
  const [resetStatus, setResetStatus] = useState<"idle"|"sending"|"sent"|"error">("idle");
  const [resetError, setResetError]   = useState("");

  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { status, user, authLoaded } = useSelector((s: RootState) => s.auth);

  useEffect(() => { if (authLoaded && user) navigate("/"); }, [authLoaded, user, navigate]);
  useEffect(() => { dispatch(listenForAuthChanges()); }, [dispatch]);

  const handleToggle = (newMode: "login" | "signup") => {
    setMode(newMode); setError("");
    setPassword(""); setConfirmPassword("");
    setShowReset(false); setResetStatus("idle"); setResetError("");
  };

  /* open reset panel, pre-fill email if already typed */
  const openReset = () => {
    setResetEmail(email);
    setResetStatus("idle");
    setResetError("");
    setShowReset(true);
  };

  const closeReset = () => {
    setShowReset(false);
    setResetStatus("idle");
    setResetError("");
    setResetEmail("");
  };

  /* ── password validation ── */
  const extractUsername = (mail: string) => mail.split("@")[0]?.toLowerCase() || "";
  const validatePassword = (pwd: string, em: string) => {
    const u = extractUsername(em);
    return {
      hasUpper:        /[A-Z]/.test(pwd),
      hasLower:        /[a-z]/.test(pwd),
      hasNumber:       /[0-9]/.test(pwd),
      hasSpecial:      /[^A-Za-z0-9]/.test(pwd),
      validLength:     pwd.length >= 6 && pwd.length <= 12,
      notEmail:        pwd.toLowerCase() !== em.toLowerCase(),
      notContainEmail: !pwd.toLowerCase().includes(em.toLowerCase()) && !pwd.toLowerCase().includes(u),
    };
  };

  const check         = validatePassword(password, email);
  const allValid      = Object.values(check).every(Boolean);
  const strengthScore = [check.hasUpper, check.hasLower, check.hasNumber, check.hasSpecial, check.validLength].filter(Boolean).length;
  const strengthLabel = ["Too Weak","Weak","Good","Strong","Very Strong"][strengthScore] || "Too Weak";
  const strengthColor = ["#FF00D4","#FF00D4","#6A00FF","#007BFF","#007BFF"][strengthScore] || "#FF00D4";

  const handleLogin = async () => {
    setError("");
    if (!email || !password) return setError("Please fill in all fields");
    try { await dispatch(loginWithEmail({ email, password })).unwrap(); navigate("/"); }
    catch (err: any) { setError(err.message || "Login failed"); }
  };

  const handleRegister = async () => {
    setError("");
    if (!email || !password)          return setError("Please fill in all fields");
    if (password !== confirmPassword) return setError("Passwords do not match");
    if (!allValid)                    return setError("Password must meet all requirements.");
    try {
      const [firstName = "", lastName = ""] = email.split("@")[0].split(".");
      await dispatch(registerWithEmail({ email, password, firstName, lastName, phoneNumber: "", profilePicture: "", username: "" })).unwrap();
      navigate("/");
    } catch (err: any) { setError(err.message || "Something went wrong"); }
  };

  const handleGoogle = async () => {
    try { await dispatch(loginWithGoogle()).unwrap(); navigate("/"); }
    catch (err: any) { setError(err.message || "Google login failed"); }
  };

  /* ── send password reset ── */
  const handleSendReset = async () => {
    setResetError("");
    if (!resetEmail.trim()) return setResetError("Please enter your email address.");
    setResetStatus("sending");
    try {
      await dispatch(sendResetEmail(resetEmail.trim())).unwrap();
      setResetStatus("sent");
    } catch (err: any) {
      setResetStatus("error");
      setResetError(err.message || "Could not send reset email. Please try again.");
    }
  };

  /* ════════════════ RENDER ════════════════ */
  return (
    <Box sx={{
      position: "relative",
      minHeight: "100vh",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      /* white bg with subtle gradient mesh */
      background: "#ffffff",
      p: 2,
      fontFamily: "'Plus Jakarta Sans', sans-serif",
      /* faint grid texture */
      backgroundImage: "linear-gradient(rgba(106,0,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(106,0,255,0.03) 1px, transparent 1px)",
      backgroundSize: "40px 40px",
    }}>
      <style>{FONT_CSS}</style>

      {/* soft radial glows in corners */}
      <div style={{ position:"fixed", top:-120, left:-80, width:500, height:500, borderRadius:"50%", background:"radial-gradient(circle, rgba(0,123,255,0.08) 0%, transparent 65%)", pointerEvents:"none" }}/>
      <div style={{ position:"fixed", bottom:-100, right:-60, width:420, height:420, borderRadius:"50%", background:"radial-gradient(circle, rgba(255,0,212,0.07) 0%, transparent 65%)", pointerEvents:"none" }}/>
      <div style={{ position:"fixed", top:"40%", right:"-5%", width:320, height:320, borderRadius:"50%", background:"radial-gradient(circle, rgba(106,0,255,0.06) 0%, transparent 65%)", pointerEvents:"none" }}/>

      {/* ── Card ── */}
      <Box sx={{
        width: "100%",
        maxWidth: 440,
        background: "rgba(255,255,255,0.92)",
        backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)",
        border: "1.5px solid #e4e2f0",
        borderRadius: "24px",
        p: { xs: 3, sm: 4 },
        boxShadow: "0 8px 48px rgba(106,0,255,0.10), 0 2px 16px rgba(0,0,0,0.06)",
        position: "relative",
        overflow: "hidden",
      }}>

        {/* subtle gradient top-border accent */}
        <div style={{
          position: "absolute", top: 0, left: 0, right: 0, height: 3,
          background: GRAD_ANIM,
          backgroundSize: "300% 100%",
          animation: "gradSpin 4s linear infinite",
          borderRadius: "24px 24px 0 0",
        }}/>

        {/* ── Logo + brand ── */}
        <Box sx={{ textAlign: "center", mb: 3.5, mt: 0.5 }}>
          {/* animated gradient ring around logo */}
          <Box sx={{ position: "relative", display: "inline-flex", mb: 1.5 }}>
            <div style={{
              position: "absolute", inset: -3,
              borderRadius: "50%",
              background: GRAD_ANIM,
              backgroundSize: "300% 100%",
              animation: "gradSpin 3s linear infinite",
              zIndex: 0,
            }}/>
            <div style={{
              position: "relative", zIndex: 1,
              background: "#fff",
              borderRadius: "50%",
              padding: 3,
              display: "inline-flex",
            }}>
              <img
                src={logoImg}
                alt="Metarelm"
                style={{
                  width: 56, height: 56,
                  borderRadius: "50%",
                  objectFit: "cover",
                  display: "block",
                }}
              />
            </div>
          </Box>

          {/* METARELM shimmer wordmark */}
          <div style={{
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontSize: 22,
            fontWeight: 900,
            letterSpacing: "0.12em",
            background: "linear-gradient(90deg, #007BFF 0%, #e8e8e8 30%, #6A00FF 55%, #FF00D4 75%, #007BFF 100%)",
            backgroundSize: "200% auto",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
            animation: "shimmer 4s linear infinite",
          }}>
            METARELM
          </div>
          <p style={{
            fontSize: 9,
            color: MUTED_CLR,
            letterSpacing: "0.22em",
            margin: "5px 0 0",
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontWeight: 500,
          }}>
            ELECTRONICS · MARKETPLACE
          </p>
        </Box>

        <AnimatePresence mode="wait">

          {/* ══════════════════════════════════════
              FORGOT PASSWORD — inline panel
          ══════════════════════════════════════ */}
          {showReset && (
            <motion.div
              key="reset"
              initial={{ opacity:0, x:24 }}
              animate={{ opacity:1, x:0 }}
              exit={{ opacity:0, x:24 }}
              transition={{ duration:0.22, ease:"easeOut" }}
            >
              {/* Back arrow */}
              <Box
                onClick={closeReset}
                sx={{ display:"flex", alignItems:"center", gap:1, cursor:"pointer", mb:3, width:"fit-content" }}
              >
                <span style={{ color: MUTED_CLR, fontSize:18, lineHeight:1 }}>←</span>
                <span
                  style={{ fontSize:10, letterSpacing:"0.14em", color: MUTED_CLR, fontFamily:"'Plus Jakarta Sans', sans-serif", transition:"color 0.2s" }}
                  onMouseEnter={e => e.currentTarget.style.color = "#6A00FF"}
                  onMouseLeave={e => e.currentTarget.style.color = MUTED_CLR}
                >
                  BACK TO LOGIN
                </span>
              </Box>

              <Typography sx={{ fontFamily:"'Plus Jakarta Sans', sans-serif", fontWeight:800, fontSize:20, color:"#07050f", letterSpacing:"-0.01em", mb:0.8 }}>
                Reset password
              </Typography>
              <Typography sx={{ fontSize:13, color: MUTED_CLR, fontFamily:"'Plus Jakarta Sans', sans-serif", mb:3.5, lineHeight:1.75 }}>
                Enter your email and we'll send a reset link instantly.
              </Typography>

              <AnimatePresence mode="wait">

                {/* sent confirmation */}
                {resetStatus === "sent" ? (
                  <motion.div key="sent" initial={{ opacity:0, y:8 }} animate={{ opacity:1, y:0 }} transition={{ duration:0.2 }}>
                    <Box sx={{ py:2, textAlign:"center" }}>
                      <Box sx={{
                        width:52, height:52,
                        borderRadius:"50%",
                        background: GRAD,
                        display:"flex", alignItems:"center", justifyContent:"center",
                        mx:"auto", mb:2,
                      }}>
                        <span style={{ color:"#fff", fontSize:22, fontWeight:700 }}>✓</span>
                      </Box>
                      <Typography sx={{ fontSize:14, fontWeight:700, color:"#07050f", fontFamily:"'Plus Jakarta Sans', sans-serif", mb:1 }}>
                        Check your inbox
                      </Typography>
                      <Typography sx={{ fontSize:12, color: MUTED_CLR, fontFamily:"'Plus Jakarta Sans', sans-serif", lineHeight:1.75 }}>
                        We sent a link to{" "}
                        <span style={{ color:"#6A00FF", fontWeight:600 }}>{resetEmail}</span>.
                        {" "}It expires in 1 hour.
                      </Typography>
                      <Button
                        fullWidth onClick={closeReset}
                        sx={{
                          mt:3.5, py:1.5, borderRadius:"12px",
                          background: GRAD,
                          backgroundSize:"200% 100%",
                          animation:"gradSpin 3s ease infinite",
                          color:"#fff",
                          fontFamily:"'Plus Jakarta Sans', sans-serif",
                          fontSize:12, fontWeight:800, letterSpacing:"0.1em",
                          textTransform:"none",
                          "&:hover":{ opacity:0.92, transform:"translateY(-1px)", boxShadow:"0 6px 20px rgba(106,0,255,0.35)" },
                          transition:"all 0.2s",
                        }}
                      >
                        Back to Login
                      </Button>
                    </Box>
                  </motion.div>

                ) : (
                  /* reset form */
                  <motion.div key="resetform" initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ duration:0.18 }}>
                    <TextField
                      label="Email address"
                      fullWidth
                      value={resetEmail}
                      onChange={e => { setResetEmail(e.target.value); setResetError(""); }}
                      placeholder="your@email.com"
                      sx={{ ...fsx(), mb: resetError ? 1 : 2.5 }}
                    />

                    {resetError && (
                      <Typography sx={{ fontSize:12, color:"#FF00D4", fontFamily:"'Plus Jakarta Sans', sans-serif", mb:2 }}>
                        {resetError}
                      </Typography>
                    )}

                    <Button
                      fullWidth
                      onClick={handleSendReset}
                      disabled={resetStatus === "sending"}
                      sx={{
                        py:1.6, borderRadius:"12px",
                        background: GRAD,
                        backgroundSize:"200% 100%",
                        animation:"gradSpin 3s ease infinite",
                        color:"#fff",
                        fontFamily:"'Plus Jakarta Sans', sans-serif",
                        fontSize:13, fontWeight:800, letterSpacing:"0.05em",
                        textTransform:"none",
                        "&:hover":{ opacity:0.92, transform:"translateY(-1px)", boxShadow:"0 6px 20px rgba(106,0,255,0.35)" },
                        "&:disabled":{ background:"#e4e2f0", color: MUTED_CLR, animation:"none" },
                        transition:"all 0.2s",
                      }}
                    >
                      {resetStatus === "sending" ? "Sending…" : "Send Reset Link"}
                    </Button>
                  </motion.div>
                )}

              </AnimatePresence>
            </motion.div>
          )}

          {/* ══════════════════════════════════════
              MAIN LOGIN / SIGNUP FORM
          ══════════════════════════════════════ */}
          {!showReset && (
            <motion.div
              key="main"
              initial={{ opacity:0, x:-24 }}
              animate={{ opacity:1, x:0 }}
              exit={{ opacity:0, x:-24 }}
              transition={{ duration:0.22, ease:"easeOut" }}
            >
              {/* ── Toggle pill ── */}
              <Box display="flex" justifyContent="center" mb={3.5}>
                <div style={{
                  width: 280, height: 48,
                  display: "flex", alignItems: "center",
                  position: "relative", cursor: "pointer",
                  border: `1.5px solid ${BORDER_CLR}`,
                  borderRadius: 100,
                  overflow: "hidden",
                  background: "#f5f4fa",
                }}>
                  <motion.div
                    key={mode}
                    initial={{ x: mode === "login" ? "-100%" : "100%" }}
                    animate={{ x: 0 }}
                    transition={{ type:"spring", stiffness:280, damping:24 }}
                    style={{
                      position: "absolute",
                      top: 3, bottom: 3,
                      left: mode === "login" ? 3 : "calc(50% + 1.5px)",
                      width: "calc(50% - 4.5px)",
                      borderRadius: 100,
                      background: GRAD,
                      backgroundSize: "200% 100%",
                      animation: "gradSpin 3s linear infinite",
                      zIndex: 1,
                    }}
                  />
                  {(["login","signup"] as const).map(m => (
                    <div
                      key={m} onClick={() => handleToggle(m)}
                      style={{
                        flex: 1, textAlign: "center",
                        fontWeight: 800, fontSize: 11,
                        letterSpacing: "0.16em",
                        color: mode === m ? "#ffffff" : MUTED_CLR,
                        zIndex: 2, userSelect: "none",
                        fontFamily: "'Plus Jakarta Sans', sans-serif",
                        transition: "color 0.3s",
                      }}
                    >
                      {m.toUpperCase()}
                    </div>
                  ))}
                </div>
              </Box>

              {/* Title */}
              <Typography sx={{
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontWeight: 800,
                fontSize: 20,
                textAlign: "center",
                mb: 3,
                color: "#07050f",
                letterSpacing: "-0.01em",
              }}>
                {mode === "login" ? "Welcome back" : "Create your account"}
              </Typography>

              {/* Email */}
              <TextField label="Email" fullWidth value={email} onChange={e => setEmail(e.target.value)} sx={fsx()}/>

              {/* Password */}
              <TextField
                label="Password"
                type={showPassword ? "text" : "password"}
                fullWidth value={password}
                onChange={e => setPassword(e.target.value)}
                error={mode === "signup" && password.length > 0 && !allValid}
                helperText={mode === "signup" && password ? `Strength: ${strengthLabel}` : ""}
                FormHelperTextProps={{ style: { color: strengthColor, fontWeight:600 } }}
                sx={{ ...fsx(), mb: mode === "signup" ? 1 : 3 }}
                InputProps={{ endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} sx={{ color: MUTED_CLR, "&:hover":{ color:"#6A00FF" } }}>
                      {showPassword ? <VisibilityOff/> : <Visibility/>}
                    </IconButton>
                  </InputAdornment>
                )}}
              />

              {/* Password requirements checklist */}
              {mode === "signup" && password && (
                <Box sx={{
                  mb: 2, p: "12px 16px",
                  background: "#f5f4fa",
                  border: `1px solid ${BORDER_CLR}`,
                  borderRadius: "12px",
                  fontSize:11, color: MUTED_CLR,
                  fontFamily:"'Plus Jakarta Sans', sans-serif",
                }}>
                  <div style={{ marginBottom:8, letterSpacing:"0.14em", fontSize:9, color: MUTED_CLR, fontWeight:700 }}>REQUIREMENTS</div>
                  <ul style={{ margin:0, padding:0, listStyle:"none" }}>
                    {[
                      { ok: check.hasUpper,        text: "At least 1 uppercase letter" },
                      { ok: check.hasLower,         text: "At least 1 lowercase letter" },
                      { ok: check.hasNumber,        text: "At least 1 number" },
                      { ok: check.hasSpecial,       text: "At least 1 special character" },
                      { ok: check.validLength,      text: "6–12 characters long" },
                      { ok: check.notEmail,         text: "Not your email" },
                      { ok: check.notContainEmail,  text: "Doesn't contain email/username" },
                    ].map((item, i) => (
                      <li key={i} style={{
                        color: item.ok ? "#6A00FF" : MUTED_CLR,
                        padding:"3px 0", fontSize:11,
                        display:"flex", alignItems:"center", gap:8,
                        fontWeight: item.ok ? 700 : 400,
                      }}>
                        <span style={{
                          width:7, height:7, borderRadius:"50%",
                          background: item.ok ? GRAD : "transparent",
                          border: `1.5px solid ${item.ok ? "transparent" : BORDER_CLR}`,
                          display:"inline-block", flexShrink:0,
                        }}/>
                        {item.text}
                      </li>
                    ))}
                  </ul>
                </Box>
              )}

              {/* Confirm password */}
              {mode === "signup" && (
                <TextField
                  label="Confirm Password" type="password" fullWidth
                  value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
                  error={confirmPassword.length > 0 && confirmPassword !== password}
                  helperText={confirmPassword.length > 0 && confirmPassword !== password ? "Passwords don't match" : ""}
                  sx={{ ...fsx(), mb:3 }}
                />
              )}

              {/* Error message */}
              {error && (
                <Typography sx={{
                  fontSize:12, textAlign:"center", mb:2,
                  color:"#FF00D4",
                  fontFamily:"'Plus Jakarta Sans', sans-serif",
                  fontWeight:600,
                }}>
                  {error}
                </Typography>
              )}

              {/* Primary CTA */}
              <Button
                fullWidth
                onClick={mode === "login" ? handleLogin : handleRegister}
                disabled={status === "loading"}
                sx={{
                  py:1.7, fontWeight:800, borderRadius:"12px",
                  background: GRAD,
                  backgroundSize:"200% 100%",
                  animation:"gradSpin 3s ease infinite",
                  color:"#fff",
                  fontFamily:"'Plus Jakarta Sans', sans-serif",
                  fontSize:13, letterSpacing:"0.05em",
                  textTransform:"none",
                  "&:hover":{ opacity:0.92, transform:"translateY(-1px)", boxShadow:"0 8px 24px rgba(106,0,255,0.4)" },
                  "&:disabled":{ background:"#e4e2f0", color: MUTED_CLR, animation:"none" },
                  transition:"all 0.2s",
                  mb:2,
                }}
              >
                {status === "loading"
                  ? (mode === "login" ? "Signing in…" : "Creating account…")
                  : (mode === "login" ? "Login" : "Sign Up")}
              </Button>

              {/* Divider */}
              <Box sx={{ display:"flex", alignItems:"center", gap:2, mb:2 }}>
                <Box sx={{ flex:1, height:"1px", background: BORDER_CLR }}/>
                <Typography sx={{ fontSize:10, color: MUTED_CLR, letterSpacing:"0.15em", fontFamily:"'Plus Jakarta Sans', sans-serif", fontWeight:600 }}>OR</Typography>
                <Box sx={{ flex:1, height:"1px", background: BORDER_CLR }}/>
              </Box>

              {/* Google CTA */}
              <Button
                fullWidth onClick={handleGoogle} variant="outlined"
                startIcon={<Google sx={{ fontSize:16 }}/>}
                sx={{
                  py:1.5, fontWeight:600, borderRadius:"12px",
                  borderColor: BORDER_CLR,
                  color: "#07050f",
                  fontFamily:"'Plus Jakarta Sans', sans-serif",
                  fontSize:13, letterSpacing:"0.02em",
                  textTransform:"none",
                  background:"#fff",
                  "&:hover":{
                    background:"#f5f4fa",
                    borderColor:"#6A00FF",
                    color:"#6A00FF",
                  },
                  transition:"all 0.2s",
                }}
              >
                Continue with Google
              </Button>

              {/* Footer links */}
              {mode === "login" && (
                <Typography sx={{ textAlign:"center", fontSize:13, mt:3, fontFamily:"'Plus Jakarta Sans', sans-serif" }}>
                  {/* Forgot password — opens inline reset panel */}
                  <span
                    onClick={openReset}
                    style={{
                      cursor:"pointer",
                      color: MUTED_CLR,
                      fontWeight:500,
                      borderBottom:`1px solid ${BORDER_CLR}`,
                      paddingBottom:2,
                      transition:"color 0.2s, border-color 0.2s",
                    }}
                    onMouseEnter={e => { e.currentTarget.style.color="#6A00FF"; e.currentTarget.style.borderBottomColor="#6A00FF"; }}
                    onMouseLeave={e => { e.currentTarget.style.color=MUTED_CLR; e.currentTarget.style.borderBottomColor=BORDER_CLR; }}
                  >
                    Forgot password?
                  </span>
                </Typography>
              )}

              {mode === "signup" && (
                <Typography sx={{ textAlign:"center", fontSize:13, mt:3, color: MUTED_CLR, fontFamily:"'Plus Jakarta Sans', sans-serif", fontWeight:500 }}>
                  Already have an account?{" "}
                  <span
                    style={{ cursor:"pointer", color:"#6A00FF", fontWeight:700, borderBottom:"1.5px solid #6A00FF", paddingBottom:1 }}
                    onClick={() => handleToggle("login")}
                  >
                    Login
                  </span>
                </Typography>
              )}

            </motion.div>
          )}

        </AnimatePresence>
      </Box>
    </Box>
  );
};

export default AuthSwitch;